import zxcvbn from 'zxcvbn';

export interface PasswordStrength {
  score: number;
  color: string;
  label: string;
  suggestions: string[];
  warning?: string;
  percentage: number;
}

export interface PasswordRequirement {
  id: string;
  label: string;
  validator: (password: string) => boolean;
}

export const passwordRequirements: PasswordRequirement[] = [
  { id: 'length', label: 'At least 12 characters', validator: (password) => password.length >= 12 },
  { id: 'uppercase', label: 'At least one uppercase letter', validator: (password) => /[A-Z]/.test(password) },
  { id: 'lowercase', label: 'At least one lowercase letter', validator: (password) => /[a-z]/.test(password) },
  { id: 'number', label: 'At least one number', validator: (password) => /\d/.test(password) },
  { id: 'special', label: 'At least one special character', validator: (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  { id: 'noRepeat', label: 'No repeating characters (e.g., aaa)', validator: (password) => !/(.)\1{2,}/.test(password) },
  { id: 'noSequential', label: 'No sequential characters (e.g., abc, 123)', validator: (password) => !/(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/.test(password.toLowerCase()) },
  { id: 'noCommon', label: 'No common words or patterns', validator: (password) => zxcvbn(password).score >= 3 },
];

export function getPasswordStrength(password: string): PasswordStrength {
  if (!password) {
    return {
      score: 0,
      color: 'bg-gray-200',
      label: 'Too Weak',
      suggestions: ['Enter a password'],
      percentage: 0,
    };
  }

  const result = zxcvbn(password);
  const metRequirements = passwordRequirements.filter(req => req.validator(password)).length;
  const percentage = (metRequirements / passwordRequirements.length) * 100;

  const strengthMap: Record<number, { color: string; label: string }> = {
    0: { color: 'bg-red-500', label: 'Very Weak' },
    1: { color: 'bg-orange-500', label: 'Weak' },
    2: { color: 'bg-yellow-500', label: 'Fair' },
    3: { color: 'bg-green-500', label: 'Strong' },
    4: { color: 'bg-green-600', label: 'Very Strong' },
  };

  return {
    score: result.score,
    color: strengthMap[result.score].color,
    label: strengthMap[result.score].label,
    suggestions: result.feedback.suggestions,
    warning: result.feedback.warning,
    percentage: Math.max(percentage, (result.score / 4) * 100),
  };
}

export function validatePassword(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const isValid = passwordRequirements.every(req => {
    const passes = req.validator(password);
    if (!passes) {
      errors.push(req.label);
    }
    return passes;
  });

  return { isValid, errors };
}

export function generateStrongPassword(): string {
  const length = 16;
  const charset = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
  };

  let password = '';
  const allChars = Object.values(charset).join('');

  // Ensure at least one character from each set
  password += charset.uppercase[Math.floor(Math.random() * charset.uppercase.length)];
  password += charset.lowercase[Math.floor(Math.random() * charset.lowercase.length)];
  password += charset.numbers[Math.floor(Math.random() * charset.numbers.length)];
  password += charset.symbols[Math.floor(Math.random() * charset.symbols.length)];

  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
}